import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import React, { ChangeEvent, FormEvent, useState } from 'react';
import { COLORS } from '../../Constants/styles';

// Google Form submission URL
const GOOGLE_FORM_ACTION =
  'https://docs.google.com/forms/d/e/1FAIpQLSfeZRgsClPPMG55qvFa82dYSOsObB1xOdQvgy64ZfkhQIpypw/formResponse';

const FIELD_IDS = {
  firstName:  'entry.1159865515',
  lastName:   'entry.1790227767',
  email:      'entry.367198791',
  phone:      'entry.555935990',
  schoolName: 'entry.1032071174',
  comments:   'entry.1105156224',
};

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  schoolName: '',
  comments: '',
};

export const ContactForm = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    const body = new URLSearchParams({
      [FIELD_IDS.firstName]:  formData.firstName,
      [FIELD_IDS.lastName]:   formData.lastName,
      [FIELD_IDS.email]:      formData.email,
      [FIELD_IDS.phone]:      formData.phone,
      [FIELD_IDS.schoolName]: formData.schoolName,
      [FIELD_IDS.comments]:   formData.comments,
    });

    try {
      // no-cors: browser sends the POST but returns an opaque response (status 0).
      // We can't read the response, but the data reaches Google Forms successfully.
      await fetch(GOOGLE_FORM_ACTION, {
        method: 'POST',
        mode: 'no-cors',
        body,
      });
    } catch {
      // A network error; still show success optimistically as the user can retry.
    }

    setFormData(EMPTY_FORM);
    setSubmitting(false);
    setSuccessOpen(true);
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ p: 4, maxWidth: 800, mx: 'auto', backgroundColor: COLORS.white, borderRadius: 4 }}
      >
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
          Your Information
        </Typography>

        <Divider sx={{ my: 4 }} />
        <Grid container spacing={2} mt={2}>

          <Grid item xs={12} sm={6}>
            <TextField required fullWidth name="firstName" label="First Name" placeholder="e.g. John" value={formData.firstName} onChange={handleChange} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField required fullWidth name="lastName" label="Last Name" placeholder="e.g. Smith" value={formData.lastName} onChange={handleChange} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField required fullWidth type="email" name="email" label="Email" placeholder="e.g. john@youremail.com" value={formData.email} onChange={handleChange} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              required fullWidth type="tel" name="phone" label="Phone"
              placeholder="e.g. +91 98765 43210"
              slotProps={{ htmlInput: { pattern: String.raw`(\+91[\s-]?)?[6-9][0-9]{9}`, title: 'Enter a valid Indian mobile number.' } }}
              value={formData.phone}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField fullWidth name="schoolName" label="School Name" placeholder="e.g. Green Valley School" value={formData.schoolName} onChange={handleChange} />
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={4} name="comments" label="Questions / Other Details" placeholder="Enter your message here" value={formData.comments} onChange={handleChange} />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{
                bgcolor: COLORS.accentLime,
                color: COLORS.accentDarkGreen,
                border: `1px solid ${COLORS.accentDarkGreen}`,
                fontWeight: 600,
                fontFamily: "'Big Shoulders Text', sans-serif",
                textDecoration: 'none',
                textTransform: 'uppercase',
                '&:hover': {
                  bgcolor: COLORS.accentDarkGreen,
                  color: COLORS.accentLime,
                  border: `1px solid ${COLORS.accentLime}`,
                },
              }}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessOpen(false)} severity="success" variant="filled">
          Thank you! We have received your response.
        </Alert>
      </Snackbar>
    </>
  );
};
